import axios from "axios";
import React, { useEffect, useState } from "react";
import WalletModal from "./WalletModal";
import {
  Wallet,
  User,
  Mail,
  Phone,
  Clock,
  ChevronDown,
  Loader2,
  AlertCircle,
  CreditCard,
  UserPlus,
  Filter,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Check,
  X,
  RotateCcw,
} from "lucide-react";
import { toast } from "react-toastify";

const WalletRequest = () => {
  const url = import.meta.env.VITE_URL;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openWalletModel, setOpenWalletModel] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState({});

  useEffect(() => {
    fetchUserWalletActivateRequest();
  }, [url]);

  const fetchUserWalletActivateRequest = async () => {
    try {
      setLoading(true);
      // ✅ CORRECT ENDPOINT
      const res = await axios.get(`${url}/api/wallet/pending-requests`, {
        withCredentials: true,
      });

      if (res?.data?.success) {
        setRequests(res.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch wallet requests", error);
      toast.error("Failed to load wallet requests");
    } finally {
      setLoading(false);
    }
  };

  // ✅ APPROVE REQUEST
  const handleApproveRequest = async (requestId) => {
    try {
      setUpdatingStatus({ [requestId]: "approving" });

      const res = await axios.post(
        `${url}/api/wallet/approve/${requestId}`,
        { adminNote: "Approved by admin" },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Request approved successfully!");
        fetchUserWalletActivateRequest(); // Refresh list
      }
    } catch (error) {
      console.error("Approve error:", error);
      toast.error(error.response?.data?.message || "Failed to approve request");
    } finally {
      setUpdatingStatus({});
    }
  };

  // ✅ REJECT/CANCEL REQUEST
  const handleRejectRequest = async (requestId) => {
    try {
      setUpdatingStatus({ [requestId]: "rejecting" });

      const res = await axios.put(
        `${url}/api/wallet/cancel/${requestId}`,
        { reason: "Request rejected by admin" },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Request rejected successfully!");
        fetchUserWalletActivateRequest();
      }
    } catch (error) {
      console.error("Reject error:", error);
      toast.error(error.response?.data?.message || "Failed to reject request");
    } finally {
      setUpdatingStatus({});
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "deactivated":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "pending":
        return <AlertCircle className="w-4 h-4" />;
      case "cancelled":
        return <XCircle className="w-4 h-4" />;
      case "deactivated":
        return <RotateCcw className="w-4 h-4" />;
      default:
        return "●";
    }
  };

  const getRequestTypeColor = (requestType) => {
    switch (requestType) {
      case "fund_add":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "activation":
      case "reactivation":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRequestTypeIcon = (requestType) => {
    switch (requestType) {
      case "fund_add":
        return <CreditCard className="w-4 h-4" />;
      case "activation":
      case "reactivation":
        return <UserPlus className="w-4 h-4" />;
      default:
        return <Wallet className="w-4 h-4" />;
    }
  };

  // Filter requests
  const filteredRequests = requests
    .filter((req) => {
      if (filterType === "all") return true;
      if (filterType === "activation")
        return req.requestType.includes("activation");
      return req.requestType === filterType;
    })
    .filter((req) => {
      if (!searchTerm) return true;
      const user = req.userId;
      const searchLower = searchTerm.toLowerCase();
      return (
        user?.username?.toLowerCase().includes(searchLower) ||
        user?.email?.toLowerCase().includes(searchLower) ||
        user?.phone?.includes(searchTerm)
      );
    });

  // Calculate stats
  const pendingRequests = requests.filter((r) => r.status === "pending");
  const pendingActivations = pendingRequests.filter((r) =>
    r.requestType.includes("activation")
  );
  const pendingFundAdds = pendingRequests.filter(
    (r) => r.requestType === "fund_add"
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mb-4" />
        <p className="text-lg text-gray-600">Loading wallet requests...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Wallet Requests Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Manage wallet activation and fund requests
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchUserWalletActivateRequest}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-2">
              <span className="text-sm text-gray-600">Total: </span>
              <span className="font-semibold text-emerald-700">
                {requests.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType("all")}
              className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                filterType === "all"
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Requests
            </button>
            <button
              onClick={() => setFilterType("activation")}
              className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                filterType === "activation"
                  ? "bg-blue-600 text-white"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100"
              }`}
            >
              <UserPlus className="w-4 h-4" />
              Activation/Reactivation
            </button>
            <button
              onClick={() => setFilterType("fund_add")}
              className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                filterType === "fund_add"
                  ? "bg-purple-600 text-white"
                  : "bg-purple-50 text-purple-700 hover:bg-purple-100"
              }`}
            >
              <CreditCard className="w-4 h-4" />
              Fund Requests
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 font-medium">
                Pending Activation
              </p>
              <p className="text-2xl font-bold text-yellow-800 mt-1">
                {pendingActivations.length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <UserPlus className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 font-medium">
                Pending Fund Requests
              </p>
              <p className="text-2xl font-bold text-purple-800 mt-1">
                {pendingFundAdds.length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">
                Total Pending
              </p>
              <p className="text-2xl font-bold text-green-800 mt-1">
                {pendingRequests.length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12 text-center">
          <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No Requests Found
          </h3>
          <p className="text-gray-500">
            There are no wallet requests matching your criteria.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        User Details
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Request Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Requested
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredRequests.map((req) => (
                      <tr
                        key={req._id}
                        className={`hover:bg-gray-50 transition-colors ${
                          req.status === "pending" ? "bg-yellow-50" : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-emerald-700" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {req?.userId?.username || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {req?.userId?.email || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${getRequestTypeColor(
                              req.requestType
                            )}`}
                          >
                            {getRequestTypeIcon(req.requestType)}
                            {req.requestType === "activation"
                              ? "Wallet Activation"
                              : req.requestType === "reactivation"
                              ? "Wallet Reactivation"
                              : "Fund Addition"}
                            {req.requestType === "fund_add" &&
                              req.requestedAmount && (
                                <span className="font-bold">
                                  ₹{req.requestedAmount}
                                </span>
                              )}
                          </div>
                          {req.userNote && (
                            <p className="text-xs text-gray-500 mt-1">
                              Note: {req.userNote}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {req.requestType === "fund_add" ? (
                              <>
                                ₹{req.requestedAmount?.toLocaleString() || "0"}
                              </>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(
                              req.status
                            )}`}
                          >
                            {getStatusIcon(req.status)}
                            {req.status.charAt(0).toUpperCase() +
                              req.status.slice(1)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2 text-gray-400" />
                            {new Date(req.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {/* Manage Wallet Button */}
                            <button
                              onClick={() => {
                                setWalletUser(req?.userId);
                                setOpenWalletModel(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-sm hover:shadow-md text-sm"
                            >
                              <Wallet className="w-3 h-3 mr-1" />
                              Wallet
                            </button>

                            {/* Approve/Reject for pending requests */}
                            {req.status === "pending" && (
                              <>
                                <button
                                  onClick={() => handleApproveRequest(req._id)}
                                  disabled={updatingStatus[req._id]}
                                  className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all text-sm disabled:opacity-50"
                                >
                                  {updatingStatus[req._id] === "approving" ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Check className="w-3 h-3 mr-1" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(req._id)}
                                  disabled={updatingStatus[req._id]}
                                  className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all text-sm disabled:opacity-50"
                                >
                                  {updatingStatus[req._id] === "rejecting" ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <X className="w-3 h-3 mr-1" />
                                  )}
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile & Tablet View */}
          <div className="lg:hidden space-y-4">
            {filteredRequests.map((req, index) => (
              <div
                key={req._id}
                className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden ${
                  req.status === "pending" ? "border-yellow-300" : ""
                }`}
              >
                {/* Header Row */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => toggleRowExpansion(req._id)}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div className="ml-4">
                      <div className="font-medium text-gray-900">
                        {req?.userId?.username || "N/A"}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getRequestTypeColor(
                            req.requestType
                          )}`}
                        >
                          {getRequestTypeIcon(req.requestType)}
                          {req.requestType === "activation"
                            ? "Activation"
                            : req.requestType === "reactivation"
                            ? "Reactivation"
                            : "Funds"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        req.status
                      )}`}
                    >
                      {getStatusIcon(req.status)}
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedRows.includes(req._id)
                          ? "transform rotate-180"
                          : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedRows.includes(req._id) && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="space-y-3 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            <Mail className="w-3 h-3 inline mr-1" />
                            Email
                          </label>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {req?.userId?.email || "N/A"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            <Phone className="w-3 h-3 inline mr-1" />
                            Phone
                          </label>
                          <p className="text-sm font-medium text-gray-900">
                            {req?.userId?.phone || "N/A"}
                          </p>
                        </div>
                      </div>

                      {req.requestType === "fund_add" &&
                        req.requestedAmount && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              <CreditCard className="w-3 h-3 inline mr-1" />
                              Requested Amount
                            </label>
                            <p className="text-lg font-bold text-purple-700">
                              ₹{req.requestedAmount}
                            </p>
                          </div>
                        )}

                      {req.userNote && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            User Note
                          </label>
                          <p className="text-sm text-gray-700">
                            {req.userNote}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Requested At
                        </label>
                        <p className="text-sm text-gray-900">
                          {new Date(req.createdAt).toLocaleDateString("en-IN", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>

                      <div className="pt-2 flex flex-col gap-2">
                        <button
                          onClick={() => {
                            setWalletUser(req?.userId);
                            setOpenWalletModel(true);
                          }}
                          className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all"
                        >
                          <Wallet className="w-4 h-4 mr-2" />
                          Manage User Wallet
                        </button>

                        {req.status === "pending" && (
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => handleApproveRequest(req._id)}
                              disabled={updatingStatus[req._id]}
                              className="inline-flex items-center justify-center px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                            >
                              {updatingStatus[req._id] === "approving" ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4 mr-2" />
                              )}
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectRequest(req._id)}
                              disabled={updatingStatus[req._id]}
                              className="inline-flex items-center justify-center px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all disabled:opacity-50"
                            >
                              {updatingStatus[req._id] === "rejecting" ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <X className="w-4 h-4 mr-2" />
                              )}
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <WalletModal
        open={openWalletModel}
        onClose={() => setOpenWalletModel(false)}
        user={walletUser}
        refreshRequests={fetchUserWalletActivateRequest}
      />
    </div>
  );
};

export default WalletRequest;
